/**
 * Collections Feature Entry
 */

import CollectionModal from './CollectionModal.js';
import CollectionAPI from './CollectionAPI.js';
import CollectionManager from './CollectionManager.js';
import CollectionSearch from './CollectionSearch.js';
import CollectionPagination from './CollectionPagination.js';
import CollectionCreateForm from './CollectionCreateForm.js';
import { showToast, escapeHtml } from './CollectionUI.js';
import { initCollectionHeaderShare } from './CollectionHeaderShare.js';

export function initCollections() {
    /* Initialize share button in public collection header (works for logged out users too) */
    initCollectionHeaderShare();
    
    if (!window.iphubCollections?.isLoggedIn) {
        return;
    }

    // Initialize dashboard if on collections page
    if (document.querySelector('.collections-dashboard')) {
        import('../collections-dashboard/CollectionsDashboard.js').then(({ initCollectionsDashboard }) => {
            initCollectionsDashboard();
        }).catch(error => {
            console.error('Failed to load collections dashboard:', error);
        });
    }

    const modal = new CollectionModal();
    if (!modal.modal) {
        return;
    }

    const api = new CollectionAPI();
    const manager = new CollectionManager(api);
    const search = new CollectionSearch(modal.modal, manager, api);
    const pagination = new CollectionPagination(modal.modal, manager);
    const createForm = new CollectionCreateForm(modal, manager);

    const listContainer = modal.modal.querySelector('[data-collections-list]');
    const loadingState = modal.modal.querySelector('[data-collection-loading]');
    const emptyState = modal.modal.querySelector('[data-collection-empty]');
    const saveButton = modal.modal.querySelector('[data-collection-save]');
    const saveSpinner = modal.modal.querySelector('[data-save-spinner]');
    const saveText = modal.modal.querySelector('[data-save-text]');
    const createButton = modal.modal.querySelector('[data-collection-create-new]');
    const errorAlert = modal.modal.querySelector('[data-collection-error]');
    const errorMessage = modal.modal.querySelector('[data-collection-error-message]');
    const retryBanner = modal.modal.querySelector('[data-collection-retry]');
    const retryMessage = modal.modal.querySelector('[data-retry-message]');
    const retryActionBtn = modal.modal.querySelector('[data-retry-action]');
    const retryDismissBtn = modal.modal.querySelector('[data-retry-dismiss]');
    let activeRetryHandler = null;

    const getCollectionsStrings = () => window.iphubCollections?.strings || {};

    if (listContainer) {
        listContainer.addEventListener('change', (event) => {
            const checkbox = event.target.closest('[data-collection-checkbox]');
            if (!checkbox) return;

            const collectionId = Number(checkbox.value);
            if (!collectionId) return;

            manager.toggleSelection(collectionId);
        });
    }

    if (saveButton) {
        saveButton.addEventListener('click', async () => {
            try {
                toggleSaveLoading(true);
                const result = await manager.persistSelection();
                const summary = result.summary || {};
                const totalAdded = summary.total_added || result.added?.length || 0;
                const totalRemoved = summary.total_removed || result.removed?.length || 0;
                const totalErrors = summary.total_errors || 0;

                if (totalAdded === 0 && totalRemoved === 0) {
                    showToast('success', getCollectionsStrings().noChanges || 'Không có thay đổi');
                } else {
                    let message = getCollectionsStrings().savedSuccess || 'Đã cập nhật danh sách yêu thích';
                    
                    /* Show detailed success message with counts */
                    if (totalAdded > 0 && totalRemoved > 0) {
                        message = `Đã thêm vào ${totalAdded} danh sách và xóa khỏi ${totalRemoved} danh sách`;
                    } else if (totalAdded > 0) {
                        message = `Đã thêm vào ${totalAdded} danh sách`;
                    } else if (totalRemoved > 0) {
                        message = `Đã xóa khỏi ${totalRemoved} danh sách`;
                    }

                    /* Show warning if there were errors */
                    if (totalErrors > 0) {
                        message += ` (${totalErrors} thao tác thất bại)`;
                        showToast('warning', message);
                    } else {
                        showToast('success', message);
                    }
                }
            } catch (error) {
                showToast('error', error.message || 'Không thể lưu danh sách');
            } finally {
                toggleSaveLoading(false);
            }
        });
    }

    if (createButton) {
        createButton.addEventListener('click', (event) => {
            event.preventDefault();
            const state = manager.getState();
            createForm.open(state.currentFdiId);
        });
    }

    modal.on('open', ({ fdiId }) => {
        manager.initialize(fdiId);
    });

    modal.on('close', () => {
        manager.clearSelection();
    });

    manager.on('state:change', (state) => {
        updateLoadingState(state.loading);
        renderCollectionsList(state);
        updateEmptyState(state);
        updateSaveButtonState(state);
    });

    manager.on('selection:change', (state) => {
        updateCheckboxStates(state);
        updateSaveButtonState(state);
    });

    manager.on('collections:error', (detail) => {
        if (!detail) return;
        showInlineError(detail.error);
        if (detail.context === 'load') {
            renderError(detail.error);
        }
        if (detail.retryable && typeof detail.retry === 'function') {
            showRetryPrompt(detail);
        } else {
            hideRetryPrompt();
        }
        showToast('error', detail.error);
    });

    manager.on('items:error', (detail) => {
        if (!detail) return;
        showInlineError(detail.error);
        if (detail.retryable && typeof detail.retry === 'function') {
            showRetryPrompt(detail);
        } else {
            hideRetryPrompt();
        }
        if (detail.context !== 'persist') {
            showToast('error', detail.error);
        }
    });

    manager.on('collection:error', (detail) => {
        if (!detail) return;
        showInlineError(detail.error);
        if (detail.retryable && typeof detail.retry === 'function') {
            showRetryPrompt(detail);
        } else {
            hideRetryPrompt();
        }
        showToast('error', detail.error);
    });

    manager.on('collections:loaded', () => {
        clearInlineError();
        hideRetryPrompt();
    });

    manager.on('collection:created', ({ collection }) => {
        clearInlineError();
        hideRetryPrompt();
        setTimeout(() => highlightCollection(collection.id), 150);
    });

    manager.on('items:added', () => {
        clearInlineError();
        hideRetryPrompt();
    });

    manager.on('items:removed', () => {
        clearInlineError();
        hideRetryPrompt();
    });

    manager.on('selection:saved', () => {
        clearInlineError();
        hideRetryPrompt();
    });

    attachSaveButtonListeners(modal);

    window.IPHubCollections = {
        modal,
        api,
        manager,
        search,
        pagination,
        createForm,
        openModal: (fdiId) => modal.open(fdiId),
        closeModal: () => modal.close(),
    };

    function renderCollectionsList(state) {
        if (!listContainer) {
            return;
        }

        const { collections, selection } = state;

        if (!collections.length) {
            listContainer.innerHTML = '';
            return;
        }

        const selectedIds = new Set(selection);
        listContainer.innerHTML = collections.map((collection) => {
            const collectionId = Number(collection.id);
            const checkboxId = `collection-${collectionId}`;
            const isChecked = selectedIds.has(collectionId);
            const isSaved = collection.has_fdi === true || collection.has_fdi === 1 || collection.has_fdi === '1';
            const isPublic = collection.is_public === true || collection.is_public === 1 || collection.is_public === '1';
            const visibilityLabel = isPublic ? 'Công khai' : 'Riêng tư';

            /* Determine thumbnail theme */
            const thumbnailTheme = isPublic ? 'green' : 'gray';

            const tagsMarkup = (collection.tags || [])
                .map((tag) => `<span class="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">${escapeHtml(tag)}</span>`)
                .join('');

            return `
                <div class="collection-item relative border border-gray-200 rounded-lg p-4 hover:border-blue-500/70 hover:bg-blue-50/40 transition-all"
                    data-collection-item
                    data-collection-id="${collectionId}"
                    data-has-fdi="${isSaved}">
                    <div class="flex items-start gap-4">
                        <input type="checkbox"
                            id="${checkboxId}"
                            class="collection-checkbox mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer"
                            data-collection-checkbox
                            value="${collectionId}"
                            ${isChecked ? 'checked' : ''}>
                        
                        <div class="collection-thumbnail" data-collection-theme="${thumbnailTheme}">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" 
                                 stroke="currentColor" stroke-width="2" stroke-linecap="round" 
                                 stroke-linejoin="round" aria-hidden="true">
                                <path d="M10 2v8l3-3 3 3V2"/>
                                <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H19a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H6.5a1 1 0 0 1 0-5H20"/>
                            </svg>
                        </div>

                        <label for="${checkboxId}" class="flex-1 cursor-pointer min-w-0">
                            <div class="flex flex-col gap-2">
                                <div class="flex items-start justify-between gap-3">
                                    <h4 class="text-sm font-semibold text-gray-900 line-clamp-1" data-collection-name>
                                        ${escapeHtml(collection.name)}
                                    </h4>
                                    <span class="px-2 py-0.5 text-xs font-medium rounded-full ${isSaved ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}"
                                        data-already-saved
                                        data-saved-value="${isSaved ? 'saved' : 'unsaved'}">
                                        ${isSaved ? '✓ Đã lưu' : 'Chưa lưu'}
                                    </span>
                                </div>
                                ${collection.description ? `<p class="text-xs text-gray-600 line-clamp-2" data-collection-description>${escapeHtml(collection.description)}</p>` : ''}
                                <div class="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                                    <span data-collection-count>
                                        ${collection.item_count || 0} mục
                                    </span>
                                    <span data-collection-visibility data-visibility-value="${isPublic ? 'public' : 'private'}">${visibilityLabel}</span>
                                    ${collection.view_count ? `<span>${collection.view_count} lượt xem</span>` : ''}
                                </div>
                                <div class="flex flex-wrap gap-1.5" data-collection-tags>${tagsMarkup}</div>
                            </div>
                        </label>
                    </div>
                </div>
            `;
        }).join('');
    }

    function updateLoadingState(isLoading) {
        if (loadingState) {
            loadingState.classList.toggle('hidden', !isLoading);
        }
    }

    function updateEmptyState(state) {
        if (!emptyState) return;
        const shouldShow = !state.loading && state.collections.length === 0;
        emptyState.classList.toggle('hidden', !shouldShow);
    }

    function updateCheckboxStates(state) {
        if (!listContainer) return;

        const selectedIds = new Set(state.selection);
        const checkboxes = listContainer.querySelectorAll('[data-collection-checkbox]');
        
        checkboxes.forEach((checkbox) => {
            const collectionId = Number(checkbox.value);
            if (collectionId) {
                checkbox.checked = selectedIds.has(collectionId);
            }
        });
    }

    function updateSaveButtonState(state) {
        if (!saveButton) return;

        const assignedSet = state.assigned;
        const selection = Array.from(state.selection);
        const assigned = Array.from(assignedSet);
        const isDirty = selection.length !== assigned.length
            || selection.some((id) => !assignedSet.has(id));

        const disabled = state.loading || !isDirty;
        saveButton.disabled = disabled;

        if (!state.loading && saveText && saveText.textContent !== 'Lưu') {
            saveText.textContent = 'Lưu';
        }
    }

    function toggleSaveLoading(isLoading) {
        if (!saveButton) return;
        saveButton.disabled = isLoading;
        if (saveSpinner) {
            saveSpinner.classList.toggle('hidden', !isLoading);
        }
        if (saveText) {
            saveText.textContent = isLoading ? 'Đang lưu...' : 'Lưu';
        }
    }

    function showInlineError(message) {
        if (!errorAlert) return;
        errorAlert.classList.remove('hidden');
        if (errorMessage) {
            errorMessage.textContent = message;
        }
    }

    function clearInlineError() {
        if (!errorAlert) return;
        errorAlert.classList.add('hidden');
        if (errorMessage) {
            errorMessage.textContent = '';
        }
    }

    function showRetryPrompt(detail) {
        if (!retryBanner || typeof detail.retry !== 'function') {
            return;
        }
        activeRetryHandler = detail.retry;
        retryBanner.classList.remove('hidden');
        if (retryMessage) {
            retryMessage.textContent = detail.error || 'Kết nối bị gián đoạn. Vui lòng thử lại.';
        }
        if (retryActionBtn) {
            retryActionBtn.onclick = () => {
                hideRetryPrompt();
                if (typeof activeRetryHandler === 'function') {
                    activeRetryHandler();
                }
            };
        }
        if (retryDismissBtn) {
            retryDismissBtn.onclick = () => hideRetryPrompt();
        }
    }

    function hideRetryPrompt() {
        if (!retryBanner) return;
        retryBanner.classList.add('hidden');
        if (retryMessage) {
            retryMessage.textContent = '';
        }
        if (retryActionBtn) {
            retryActionBtn.onclick = null;
        }
        if (retryDismissBtn) {
            retryDismissBtn.onclick = null;
        }
        activeRetryHandler = null;
    }

    function renderError(message) {
        if (!listContainer) {
            showToast('error', message || 'Đã xảy ra lỗi');
            return;
        }

        listContainer.innerHTML = `
            <div class="text-center py-8 text-red-500">
                <p>${escapeHtml(message || 'Đã xảy ra lỗi')}</p>
            </div>
        `;
    }

    function highlightCollection(collectionId) {
        const element = modal.modal.querySelector(`[data-collection-id="${collectionId}"]`);
        if (!element) {
            return;
        }
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.classList.add('ring-2', 'ring-blue-500', 'ring-opacity-60', 'bg-blue-50');
        setTimeout(() => {
            element.classList.remove('ring-2', 'ring-blue-500', 'ring-opacity-60', 'bg-blue-50');
        }, 2500);
    }

    // Legacy buttons on listing pages
    function attachSaveButtonListeners(modalInstance) {
        document.addEventListener('click', (event) => {
            const saveBtn = event.target.closest('[data-open-collections], .save-btn, .add-to-collection');
            if (!saveBtn) return;

            const fdiId = saveBtn.dataset.fdiId || saveBtn.dataset.id;
            if (!fdiId) return;

            event.preventDefault();
            modalInstance.open(Number(fdiId));
        });
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCollections);
} else {
    initCollections();
}
